import "./Documents.scss";

import { useAtom } from "jotai";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";

import DocumentMint from "@/assets/ux/DocumentMint.svg?react";
import { Tablist } from "@/components/Interface/Tablist/Tablist";
import { TabLayout } from "@/components/Layout/BaseLayouts/TabLayout";
import { PortalHeading } from "@/components/Portal/components/PortalHeading/PortalHeading";
import { useIsMobile } from "@/hooks/useIsMobile";
import { usePaymentsTabLabel } from "@/hooks/usePaymentsTabLabel";
import {
  documentsEditStateAtom,
  documentsEditStateAtomInitialState,
} from "@/utils/atoms";

/**
 * Bills section: an icon header, the three document tabs (Bills,
 * Connection overview, Means of payment) and their routed content, in a
 * TabLayout. The chrome is hidden in place during mobile multi-select, so the
 * routed outlet never remounts.
 */
export const Documents: FC = () => {
  const { t } = useTranslation("portal");
  const isMobile = useIsMobile();
  const [documentsEdit, setDocumentsEdit] = useAtom(documentsEditStateAtom);
  const paymentsTabLabel = usePaymentsTabLabel();

  useEffect(() => {
    return () => {
      setDocumentsEdit(documentsEditStateAtomInitialState);
    };
  }, [setDocumentsEdit]);

  const isStandaloneMobileFlow =
    isMobile &&
    (documentsEdit.billsSelecting || documentsEdit.itemizedBillsSelecting);

  return (
    <TabLayout
      hideChrome={isStandaloneMobileFlow}
      header={
        <PortalHeading heading={t("common.bills")} icon={<DocumentMint />} />
      }
      tabs={
        <Tablist
          ariaLabel={t("common.bills")}
          tabs={[
            {
              target: "bills",
              disabled: false,
              text: t("common.bills"),
            },
            {
              target: "itemized-bills",
              disabled: false,
              text: t("documents.tabItemizedBill.title"),
            },
            {
              target: "payments",
              disabled: false,
              text: paymentsTabLabel,
            },
          ]}
        />
      }
    >
      <div id="documents-container">
        <Outlet />
      </div>
    </TabLayout>
  );
};
